import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';

interface DashboardStats {
  inventoryCount: number;
  pendingBookings: number;
  unreadMessages: number;
}

interface RecentBooking {
  id: string;
  customer_name: string;
  service_type: string;
  appointment_date: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  total_price: number;
}

interface LowStockItem {
  id: string;
  brand: string;
  model: string;
  size: string;
  stock_quantity: number;
}

interface DashboardData {
  stats: DashboardStats;
  recentBookings: RecentBooking[];
  lowStockItems: LowStockItem[];
}

export const useDashboardData = () => {
  const [data, setData] = useState<DashboardData>({
    stats: {
      inventoryCount: 0,
      pendingBookings: 0,
      unreadMessages: 0
    },
    recentBookings: [],
    lowStockItems: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  
  const { user } = useAuthStore();

  const fetchBusinessId = async () => {
    if (!user?.id) return null;
    
    try {
      const { data: businessData, error } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_id', user.id)
        .single();
      
      if (error) throw error;
      return businessData?.id || null;
    } catch (err) {
      console.error('Error fetching business ID:', err);
      return null;
    }
  };

  const fetchDashboardData = async () => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      setError(null);

      // Fetch inventory count and low stock items
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('tyre_inventory')
        .select('id, brand, model, size, quantity')
        .eq('business_id', businessId);

      if (inventoryError) throw inventoryError;

      // Fetch bookings data
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          id,
          appointment_date,
          service_type,
          status,
          total_price,
          customer_id,
          created_at
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (bookingsError) throw bookingsError;

      // Get customer names for recent bookings
      const recentBookingsWithCustomers = await Promise.all(
        (bookingsData || []).slice(0, 3).map(async (booking) => {
          const { data: customerData } = await supabase
            .from('user_profiles')
            .select('full_name, first_name, last_name')
            .eq('id', booking.customer_id)
            .single();

          const customerName = customerData?.full_name || 
            `${customerData?.first_name || ''} ${customerData?.last_name || ''}`.trim() ||
            'Unknown Customer';

          return {
            id: booking.id,
            customer_name: customerName,
            service_type: booking.service_type || 'Tyre Installation',
            appointment_date: booking.appointment_date,
            status: booking.status as 'pending' | 'confirmed' | 'completed' | 'cancelled',
            total_price: booking.total_price || 0
          };
        })
      );

      // Calculate stats
      const inventoryCount = inventoryData?.length || 0;
      const pendingBookings = bookingsData?.filter(b => b.status === 'pending').length || 0;
      
      // Filter low stock items (quantity <= 2)
      const lowStockItems = (inventoryData || [])
        .filter(item => (item.quantity || 0) <= 2)
        .map(item => ({
          id: item.id,
          brand: item.brand || 'Unknown Brand',
          model: item.model || 'Unknown Model',
          size: item.size || 'Unknown Size',
          stock_quantity: item.quantity || 0
        }));

      setData({
        stats: {
          inventoryCount,
          pendingBookings,
          unreadMessages: 0 // This will be handled by existing messaging service
        },
        recentBookings: recentBookingsWithCustomers,
        lowStockItems
      });

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch business ID when user changes
  useEffect(() => {
    const initializeBusinessId = async () => {
      if (user?.id) {
        const id = await fetchBusinessId();
        setBusinessId(id);
      } else {
        setBusinessId(null);
      }
    };

    initializeBusinessId();
  }, [user?.id]);

  // Fetch dashboard data when business ID is available
  useEffect(() => {
    if (businessId) {
      fetchDashboardData();
    }
  }, [businessId]);

  const refetch = () => {
    if (businessId) {
      fetchDashboardData();
    }
  };

  return {
    data,
    loading,
    error,
    businessId,
    refetch
  };
};